import { useRef, useCallback, useEffect, useState } from "react";
import type { MediaController } from "./types";

export function useHTMLMediaAdapter(
  type: "video" | "audio" | "recording",
  src: string
): {
  ref: React.RefObject<HTMLVideoElement | HTMLAudioElement>;
  controller: MediaController | null;
} {
  const ref = useRef<HTMLVideoElement | HTMLAudioElement>(null);
  const [ready, setReady] = useState(false);
  const timeUpdateCallbacks = useRef<Set<(time: number) => void>>(new Set());
  const stateChangeCallbacks = useRef<Set<(playing: boolean) => void>>(new Set());

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const handleCanPlay = () => setReady(true);
    const handleTimeUpdate = () => {
      timeUpdateCallbacks.current.forEach((cb) => cb(el.currentTime));
    };
    const handlePlay = () => {
      stateChangeCallbacks.current.forEach((cb) => cb(true));
    };
    const handlePause = () => {
      stateChangeCallbacks.current.forEach((cb) => cb(false));
    };

    el.addEventListener("canplay", handleCanPlay);
    el.addEventListener("timeupdate", handleTimeUpdate);
    el.addEventListener("play", handlePlay);
    el.addEventListener("pause", handlePause);

    return () => {
      el.removeEventListener("canplay", handleCanPlay);
      el.removeEventListener("timeupdate", handleTimeUpdate);
      el.removeEventListener("play", handlePlay);
      el.removeEventListener("pause", handlePause);
    };
  }, []);

  const play = useCallback(() => {
    ref.current?.play();
  }, []);

  const pause = useCallback(() => {
    ref.current?.pause();
  }, []);

  const seek = useCallback((seconds: number) => {
    if (ref.current) {
      ref.current.currentTime = seconds;
    }
  }, []);

  const getCurrentTime = useCallback(() => {
    return ref.current?.currentTime ?? 0;
  }, []);

  const getDuration = useCallback(() => {
    return ref.current?.duration ?? 0;
  }, []);

  const isPlaying = useCallback(() => {
    return ref.current ? !ref.current.paused : false;
  }, []);

  const onTimeUpdate = useCallback((cb: (time: number) => void) => {
    timeUpdateCallbacks.current.add(cb);
    return () => {
      timeUpdateCallbacks.current.delete(cb);
    };
  }, []);

  const onStateChange = useCallback((cb: (playing: boolean) => void) => {
    stateChangeCallbacks.current.add(cb);
    return () => {
      stateChangeCallbacks.current.delete(cb);
    };
  }, []);

  const controller: MediaController | null = ready
    ? {
        play,
        pause,
        seek,
        getCurrentTime,
        getDuration,
        isPlaying,
        onTimeUpdate,
        onStateChange,
        src,
        type,
      }
    : null;

  return { ref, controller };
}
